/**
 * ProjectBrowser - modal overlay listing all projects with create/delete/switch.
 *
 * Three views:
 *   - "list"   : grid of project cards. Active project is highlighted. Cards
 *                show name, device label, fit, and item count. Click a card to
 *                switch to it. New Project button at top. Delete via x per row.
 *   - "new"    : create form - name plus a combo picker.
 *   - "device" : change an EXISTING project's device/orientation. Same picker.
 *
 * The modal is dismissible via the X (or Esc), UNLESS there are no projects
 * at all - in that case the user MUST create one before they can use the app.
 *
 * WHY THE PICKER LOOKS LIKE THIS (2026-07-31). It used to be two unrelated
 * radio groups (Device, Orientation) plus a bare "1284 x 2778px" readout. The
 * operator picked iPhone by accident on an iPad and got a rig rendered at 27%,
 * too small to read the menu that would have let them undo it. Nothing in that
 * form connected the choice to the consequence: a raw pixel pair does not tell
 * anyone their rig is about to be illegible.
 *
 * So the picker now shows, per combo, the actual PROPORTIONS of the canvas and
 * the scale it will render at ON THE SCREEN YOU ARE HOLDING - computed with
 * `fitScale`, the same function App.tsx uses, so the promise and the delivery
 * cannot drift. A combo that would render unreadably is marked, and the one
 * that fits best is badged. The wrong choice is now visibly wrong BEFORE you
 * commit to it, which is the only point at which it is cheap to fix.
 */

import { createSignal, createMemo, onMount, onCleanup, Show, For } from 'solid-js';
import {
  projects,
  activeProjectId,
  createProject,
  deleteProject,
  switchProject,
  updateProjectDevice,
  renameProject,
  duplicateProject,
} from '../layout/projectStore.ts';
import {
  DEVICES,
  type DeviceId,
  type Orientation,
  canvasSize,
  deviceLabel,
  fitScale,
  READABLE_SCALE_FLOOR, canvasSizeOf } from '../layout/devices.ts';
import { importEverythingFromText } from '../bundle/serialize.ts';
import { pickFile, readFileText } from '../bundle/download.ts';
import { pushToast } from '../ui/toast.ts';
import { recentProjectEntries } from '../navigate/recentProjects.ts';
import { modifiedAtOf } from '../layout/projectMerge.ts';

interface ProjectBrowserProps {
  /** When false, the X close button is hidden - user must create a project. */
  dismissible: boolean;
  onClose: () => void;
  /** Render inside Navigate's Project pane (2026-10-05, app-study.html): no
   *  backdrop, no window of its own; the pane gives the title. */
  embedded?: boolean;
  /** Which view to mount into. Defaults to 'list'; the logo menu's "New
   *  project" row passes 'new' to jump straight to the creation form. Zero
   *  projects always forces 'new' regardless - there is nothing to list. */
  initialView?: 'list' | 'new' | 'welcome';
  /** First run only: the welcome view's "Connect to OPBridge" button. */
  onConnect?: () => void;
}

const ORIENTATIONS: Orientation[] = ['landscape', 'portrait'];

interface Combo {
  device: DeviceId;
  orientation: Orientation;
  label: string;
  size: { w: number; h: number };
}

const COMBOS: Combo[] = DEVICES.flatMap((d) =>
  ORIENTATIONS.map((o) => ({
    device: d.id,
    orientation: o,
    label: `${d.label} - ${o}`,
    size: canvasSize(d.id, o),
  })),
);

export function ProjectBrowser(props: ProjectBrowserProps) {
  // Newest first: what this device opened last, then what was edited last
  // anywhere. Navigate's home had a "Recent projects" list until 2026-10-05;
  // the study puts switching here, so the order carries it instead.
  const ordered = createMemo(() => {
    const opened = new Map(recentProjectEntries().map((e) => [e.id, e.at] as const));
    return [...projects()].sort((a, b) =>
      (opened.get(b.id) ?? 0) - (opened.get(a.id) ?? 0) || modifiedAtOf(b) - modifiedAtOf(a));
  });
  // 'welcome' opens on first run (OPApps UX audit A4, operator 2026-09-28):
  // say what OPButtons is and what it needs BEFORE asking for a project name.
  // It is also Navigate's "Getting started" card (2026-09-30), so an explicit
  // request is honoured when projects exist too - App only ever passes it on
  // first run or from that card.
  const [view, setView] = createSignal<'list' | 'new' | 'device' | 'welcome' | 'rename'>(
    projects().length === 0
      ? (props.initialView === 'welcome' ? 'welcome' : 'new')
      : (props.initialView ?? 'list'),
  );
  const [confirmDeleteId, setConfirmDeleteId] = createSignal<string | null>(null);
  // Rename + duplicate (2026-10-05): neither existed in the UI; the help
  // articles would otherwise have had to say "you can't". Rename has its own
  // view, like Change device, so no text field lives inside the <For> list
  // (DEC-184).
  const [renameDraft, setRenameDraft] = createSignal('');

  /** "Restore from a backup" (welcome view): the same restore as Settings >
   *  Import everything. Closes the browser once a project is back. */
  async function restoreFromBackup(): Promise<void> {
    const file = await pickFile('.json,application/json');
    if (!file) return;
    let text: string;
    try {
      text = await readFileText(file);
    } catch (e) {
      pushToast('error', `Could not read file: ${(e as Error).message}`);
      return;
    }
    const r = importEverythingFromText(text);
    pushToast(r.ok ? 'ok' : 'error', r.message, r.ok ? 6000 : undefined);
    if (r.ok && projects().length > 0) props.onClose();
  }
  /** Which project the 'device' view is editing. */
  const [editingId, setEditingId] = createSignal<string | null>(null);

  // ── New-project form state ────────────────────────────────────
  const [name, setName] = createSignal('Untitled');
  const [device, setDevice] = createSignal<DeviceId>('ipad');
  const [orientation, setOrientation] = createSignal<Orientation>('landscape');

  // ── Live viewport measurement ─────────────────────────────────
  // The fit prediction is worthless unless it is measured against the box the
  // shell actually gets. `.viewport-fit` is that box; window dimensions would
  // over-report on any screen with browser chrome, which is every iPad.
  const [avail, setAvail] = createSignal({ w: 0, h: 0 });
  function measure() {
    const el = document.querySelector('.viewport-fit') as HTMLElement | null;
    if (el) setAvail({ w: el.clientWidth, h: el.clientHeight });
    else setAvail({ w: window.innerWidth, h: window.innerHeight });
  }
  onMount(() => {
    measure();
    window.addEventListener('resize', measure);
    onCleanup(() => window.removeEventListener('resize', measure));
  });

  const scaleFor = (c: { w: number; h: number }) => fitScale(c, avail());
  /** The combo that renders largest on this screen. Badged so the operator
   *  has a positive recommendation, not just a list of warnings. */
  const bestCombo = createMemo<Combo | undefined>(() => {
    let best: Combo | undefined;
    for (const c of COMBOS) {
      if (!best || scaleFor(c.size) > scaleFor(best.size)) best = c;
    }
    return best;
  });

  const selectedSize = createMemo(() => canvasSize(device(), orientation()));
  const selectedScale = createMemo(() => scaleFor(selectedSize()));

  function onCreate() {
    createProject({ name: name(), device: device(), orientation: orientation() });
    setName('Untitled');
    setDevice('ipad');
    setOrientation('landscape');
    setView('list');
    props.onClose();
  }

  function onPickProject(id: string) {
    switchProject(id);
    props.onClose();
  }

  function openDeviceEditor(id: string) {
    const p = projects().find((x) => x.id === id);
    if (!p) return;
    setDevice(p.device);
    setOrientation(p.orientation);
    setEditingId(id);
    setView('device');
  }

  function onApplyDevice() {
    const id = editingId();
    if (id) updateProjectDevice(id, device(), orientation());
    setEditingId(null);
    setView('list');
  }

  function openRename(id: string) {
    const p = projects().find((x) => x.id === id);
    if (!p) return;
    setRenameDraft(p.name);
    setEditingId(id);
    setView('rename');
  }

  function onApplyRename() {
    const id = editingId();
    if (id) renameProject(id, renameDraft());
    setEditingId(null);
    setView('list');
  }

  function onDelete(id: string) {
    if (confirmDeleteId() === id) {
      deleteProject(id);
      setConfirmDeleteId(null);
      if (projects().length === 0) setView('new');
    } else {
      setConfirmDeleteId(id);
      setTimeout(() => setConfirmDeleteId((c) => (c === id ? null : c)), 3000);
    }
  }

  /** The combo picker, shared by the create and change-device views. */
  function ComboPicker() {
    return (
      <>
        <div class="pb-combos">
          <For each={COMBOS}>
            {(c) => {
              const selected = () => device() === c.device && orientation() === c.orientation;
              const scale = () => scaleFor(c.size);
              const unreadable = () => scale() < READABLE_SCALE_FLOOR;
              const isBest = () => bestCombo()?.device === c.device
                && bestCombo()?.orientation === c.orientation;
              // Proportional thumbnail: longest edge pinned to 54px so the
              // shapes are directly comparable to each other. This is the bit
              // that makes an iPhone-portrait pick look obviously different
              // from an iPad-landscape one at a glance.
              const thumb = () => {
                const max = 54;
                const k = max / Math.max(c.size.w, c.size.h);
                return { w: Math.round(c.size.w * k), h: Math.round(c.size.h * k) };
              };
              return (
                <button
                  type="button"
                  class="pb-combo"
                  data-selected={selected() ? 'true' : 'false'}
                  data-unreadable={unreadable() ? 'true' : 'false'}
                  onClick={() => { setDevice(c.device); setOrientation(c.orientation); }}
                >
                  <span class="pb-combo-thumb-box">
                    <span
                      class="pb-combo-thumb"
                      style={{ width: `${thumb().w}px`, height: `${thumb().h}px` }}
                    />
                  </span>
                  <span class="pb-combo-label">{c.label}</span>
                  <span class="pb-combo-dims">{c.size.w} x {c.size.h}</span>
                  <span class="pb-combo-fit">
                    {Math.round(scale() * 100)}% on this screen
                  </span>
                  <Show when={isBest()}>
                    <span class="pb-combo-badge">Best fit</span>
                  </Show>
                </button>
              );
            }}
          </For>
        </div>

        <Show when={selectedScale() < READABLE_SCALE_FLOOR}>
          <p class="pb-warn" role="alert">
            This renders at <strong>{Math.round(selectedScale() * 100)}%</strong> on
            the screen you are using now, which is likely too small to read or tap.
            You can still choose it - a layout designed for another device is a
            legitimate thing to build - but edit it on that device.
          </p>
        </Show>
      </>
    );
  }

  return (
    <div class="project-browser-backdrop" data-embedded={props.embedded ? 'true' : undefined}>
      <div class="project-browser" role={props.embedded ? 'region' : 'dialog'} aria-label="Projects" data-embedded={props.embedded ? 'true' : undefined}>
        <header class="project-browser-header" hidden={props.embedded && view() === 'list'}>
          <h2>
            {view() === 'list' ? 'Projects' : view() === 'new' ? 'New project' : view() === 'welcome' ? 'Welcome to OPButtons' : view() === 'rename' ? 'Rename project' : 'Change device'}
          </h2>
          <div class="project-browser-header-actions">
            <Show when={view() !== 'list' && projects().length > 0}>
              <button
                type="button"
                class="pb-link"
                onClick={() => { setEditingId(null); setView('list'); }}
              >
                Back
              </button>
            </Show>
            <Show when={props.dismissible}>
              <button
                type="button"
                class="pb-close"
                onClick={props.onClose}
                aria-label="Close"
              >
                ×
              </button>
            </Show>
          </div>
        </header>

        <Show when={view() === 'list'}>
          <div class="project-list">
            <button
              type="button"
              class="project-card project-card-new"
              onClick={() => setView('new')}
            >
              <span class="project-card-plus">+</span>
              <span>New project</span>
            </button>
            <For each={ordered()}>
              {(p) => {
                const size = canvasSizeOf(p);
                const isActive = () => p.id === activeProjectId();
                const itemCount = p.pages.reduce((n, pg) => n + pg.items.length, 0);
                const pageCount = p.pages.length;
                const scale = () => scaleFor(size);
                return (
                  <div
                    class="project-card"
                    data-active={isActive() ? 'true' : 'false'}
                    data-unreadable={scale() < READABLE_SCALE_FLOOR ? 'true' : 'false'}
                    onClick={() => onPickProject(p.id)}
                  >
                    <div class="project-card-name">{p.name}</div>
                    <div class="project-card-meta">
                      {deviceLabel(p.device, p.orientation)}
                      <br />
                      {size.w} × {size.h}px · {pageCount} page{pageCount === 1 ? '' : 's'} · {itemCount} item{itemCount === 1 ? '' : 's'}
                      <Show when={scale() < READABLE_SCALE_FLOOR}>
                        <br />
                        <span class="project-card-warn">
                          renders at {Math.round(scale() * 100)}% here
                        </span>
                      </Show>
                    </div>
                    <button
                      type="button"
                      class="project-card-device"
                      onClick={(e) => { e.stopPropagation(); openDeviceEditor(p.id); }}
                      aria-label="Change device"
                      title="Change device / orientation"
                    >
                      ⤢
                    </button>
                    <button
                      type="button"
                      class="project-card-delete"
                      data-confirm={confirmDeleteId() === p.id ? 'true' : 'false'}
                      onClick={(e) => { e.stopPropagation(); onDelete(p.id); }}
                      aria-label="Delete project"
                      title={confirmDeleteId() === p.id ? 'Tap again to confirm' : 'Delete'}
                    >
                      {confirmDeleteId() === p.id ? '!' : '×'}
                    </button>
                    <div class="project-card-actions">
                      <button type="button" class="pb-link" onClick={(e) => { e.stopPropagation(); openRename(p.id); }}>Rename</button>
                      <button type="button" class="pb-link" onClick={(e) => { e.stopPropagation(); duplicateProject(p.id); }}>Duplicate</button>
                    </div>
                  </div>
                );
              }}
            </For>
          </div>
        </Show>

        <Show when={view() === 'welcome'}>
          <div class="project-new-form pb-welcome">
            <p>
              You're holding more than a controller. OPButtons turns this tablet or phone into
              your own buttons, faders and pads for Ableton Live - and for your pedals, lights
              and stream.
            </p>
            <p class="pb-note">
              It needs <strong>OPBridge</strong> running on the computer that runs Ableton.
              OPBridge and the Ableton devices it uses will be downloadable from
              overpoweredapps.net.
            </p>
            <div class="pb-form-actions">
              <Show when={props.onConnect}>
                <button type="button" class="pb-link" onClick={() => props.onConnect?.()}>
                  Connect to OPBridge
                </button>
              </Show>
              {/* A wiped or new device restores from an "Export everything"
                  file here (plan 3.2, 2026-10-01). Without it the only way to
                  reach Import was to make a throwaway project first. */}
              <button type="button" class="pb-link" onClick={() => { void restoreFromBackup(); }}>
                Restore from a backup
              </button>
              <button type="button" class="pb-create" onClick={() => setView('new')}>
                Start a project
              </button>
            </div>
          </div>
        </Show>

        <Show when={view() === 'new'}>
          <div class="project-new-form">
            <label class="pb-field">
              <span>Name</span>
              <input
                type="text"
                value={name()}
                onInput={(e) => setName(e.currentTarget.value)}
                autofocus
              />
            </label>

            <ComboPicker />

            <div class="pb-form-actions">
              <button type="button" class="pb-create" onClick={onCreate}>
                Create
              </button>
            </div>
          </div>
        </Show>

        <Show when={view() === 'rename'}>
          <div class="project-new-form">
            <label class="pb-field">
              <span>Name</span>
              <input
                type="text"
                value={renameDraft()}
                onInput={(e) => setRenameDraft(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') onApplyRename(); }}
                autofocus
              />
            </label>
            <div class="pb-form-actions">
              <button type="button" class="pb-create" onClick={onApplyRename}>
                Save
              </button>
            </div>
          </div>
        </Show>

        <Show when={view() === 'device'}>
          <div class="project-new-form">
            <p class="pb-note">
              Changing the canvas does not move or delete any items. Items keep
              their coordinates, so a smaller canvas can push some of them out
              of view until you move them back.
            </p>

            <ComboPicker />

            <div class="pb-form-actions">
              <button type="button" class="pb-create" onClick={onApplyDevice}>
                Apply
              </button>
            </div>
          </div>
        </Show>
      </div>
    </div>
  );
}
